import { map, of } from 'rxjs';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/bh-util/server/entity-reads.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { type NarrowedEntity, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer, saveEntityData } from '@adornis/bh-util/util.js';
import { getParticipationsByID } from '@adornis/buchungsheld-db/api/participation-helper.js';
import { getStammdatenByUserAndInstitution } from '@adornis/buchungsheld-db/api/stammdaten.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { notifyBestaetigungWorkshopRegistrierung } from '@adornis/buchungsheld-db/notification/bestaetigung-workshop-registrierung.js';
import { BHSDOrderNr } from '../payment/BHSDOrderNr.js';
import { SDStammdaten } from '../stammdaten/sd-stammdaten-plugin.js';
import { SDInvoiceReceiverEnum } from '../payment/invoice-receiver.js';
import { createPaymentFromParticipations } from '../payment/helper.js';
import { getProductPrice, SDInvoice } from '../payment/sd-payment-plugin.js';
import { isSDInternMail } from '../payment/sd-email.js';

export const paymentPluginSdPay = registerMutation({
  type: () => String,
  operationName: 'paymentPluginSdPay',
  params: [
    { type: () => String, name: 'institutionID' },
    { type: () => [Participation], name: 'participationIds' },
  ],
  resolve: (institutionID: string, _participations: NarrowedEntity<Participation, { _id: 1; productID: 1 }>[]) => {
    return async () => {
      const participations = await getParticipationsByID(_participations.map(pa => pa._id))({
        _id: 1,
        userID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
          username: 1,
          whatsappNotification: 1,
          whatsappPhone: 1,
          suspentions: { _id: 1, institutionID: 1 },
          receivesNewsletter: 1,
        },
        productID: 1,
        product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
        productPriceForUser: { unitAmount: 1, currency: 1 },
      });

      const user = await getCurrentUserServer();
      if (!user) throw new Error('user should be loggedIn');

      // create Payment
      const stammdaten = await getStammdatenByUserAndInstitution<SDStammdaten>(
        user._id,
        institutionID,
      )(SDStammdaten.allFields);
      if (!stammdaten) throw new Error('could not find stammdaten');
      const isIntern = isSDInternMail(user.email) && stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL;

      const payment = await createPaymentFromParticipations(institutionID, participations, getProductPrice);
      payment.orderNr = await BHSDOrderNr.create(institutionID, isIntern ? 'intern' : 'extern');
      payment.userReference.userID = user._id;

      const paymentID = A.getGloballyUniqueID();
      payment._id = paymentID;
      await saveEntityData(payment);
      logger.info({ paymentID }, '(sd) payment created');

      // send Mails
      await notifyBestaetigungWorkshopRegistrierung(institutionID, user, participations);

      // // create Invoice
      // const invoiceID = A.getGloballyUniqueID();
      // const invoice = new SDInvoice({ _id: invoiceID, paymentID, userID: user._id, institutionID });

      // const createInvoicePdf = async (component: string, filenameType: string, params: any) => {
      //   const buffer = await printComponent(component, {
      //     user,
      //     timeout: 25 * 1000,
      //     queryParams: { payment: payment._id, ...params },
      //   });

      //   return await AdornisFile.createFromBuffer(buffer, `invoice-sd-${filenameType}-${invoiceID}.pdf`);
      // };

      // await (async () => {
      //   if (stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL) {
      //     if (!isSDInternMail(user.email)) throw new Error('invalid');
      //     const invoiceCustomer = await createInvoicePdf('sd-invoice-customer-formular3', 'customer', {
      //       invoiceProjectNumber: '64003000',
      //       visitorAddress: 'house_of_labor',
      //       invoiceTax: 0,
      //       invoiceReceiverAddress: 'user',
      //       invoiceKontierungsstempel: false,
      //       otherAttachement: 'ilv-formular',
      //     } satisfies SDInvoiceMailData);

      //     // ilv-formualr
      //     const filePath = join(dirname(fileURLToPath(import.meta.url)), relativeTemplaterFilePath);
      //     const templater = AdornisTemplater.fromFile(filePath);

      //     templater.render({
      //       date: payment.createdAt.toFormat('dd.LL.yyyy'),
      //       orderNr: payment.orderNr.getOrderNr(),
      //       year: payment.createdAt.year.toString(),
      //       amountInEuro: (payment.amount / 100).toFixed(2),
      //     });
      //     const pdfBuffer = await templater.toPdfBuffer();
      //     const pdfIlvFile = await AdornisFile.createFromBuffer(pdfBuffer, `invoice-sd-ilv-${invoiceID}.pdf`);

      //     invoice.pdfID = invoiceCustomer._id;
      //     invoice.ilvFormularID = pdfIlvFile._id;

      //     return;
      //   }

      //   const isDepartmentMedizin = stammdaten.department === SDInstitutionEnum.SDMEDIZIN;
      //   const isDepartmentUniklinikum = stammdaten.department === SDInstitutionEnum.SDUNIKLINIKUM;

      //   if (
      //     (isDepartmentMedizin || isDepartmentUniklinikum) &&
      //     stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDEXTERNAL
      //   ) {
      //     const config = {
      //       invoiceProjectNumber: '64120700',
      //       visitorAddress: 'bockenheim',
      //       invoiceTax: 0,
      //       invoiceReceiverAddress: isDepartmentMedizin ? 'med' : 'dentMed',
      //       invoiceKontierungsstempel: false,
      //       otherAttachement: null,
      //     } satisfies SDInvoiceMailData;

      //     const customerForm = isDepartmentMedizin ? 'sd-invoice-customer-formular5' : 'sd-invoice-customer-formular6';
      //     const invoiceCustomer = await createInvoicePdf(customerForm, 'customer', config);
      //     const invoiceInternal = await createInvoicePdf(customerForm, 'internal', {
      //       ...config,
      //       invoiceKontierungsstempel: true,
      //     });

      //     invoice.mailReceiver = 'kowatsch@sd.uni-frankfurt.de';
      //     invoice.pdfID = invoiceCustomer._id;
      //     invoice.pdfInternalID = invoiceInternal._id;
      //     return;
      //   }

      //   // @ts-expect-error first check makes this impossible to be true
      //   if (stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL) throw new Error('invalid');

      //   if (isSDInternMail(user.email) && stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDEXTERNAL)
      //     throw new Error('invalid');

      //   const config = {
      //     invoiceProjectNumber: '64120700',
      //     visitorAddress: 'house_of_labor',
      //     invoiceTax: 0,
      //     invoiceReceiverAddress: 'user',
      //     invoiceKontierungsstempel: false,
      //     otherAttachement: null,
      //   } satisfies SDInvoiceMailData;

      //   const invoiceCustomer = await createInvoicePdf('sd-invoice-customer-formular1', 'customer', config);
      //   const invoiceInternal = await createInvoicePdf('sd-invoice-customer-formular1', 'internal', {
      //     ...config,
      //     invoiceKontierungsstempel: true,
      //   });

      //   invoice.pdfID = invoiceCustomer._id;
      //   invoice.pdfInternalID = invoiceInternal._id;
      //   return;
      // })();

      // await invoice.create();

      return paymentID;
    };
  },
});

export const getSDInvoicesByIDs = registerQuery({
  operationName: 'getSDInvoicesByIDs',
  type: () => [SDInvoice],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<SDInvoice>>(gqlFields: SelSet) =>
      readEntitiesByIDs(SDInvoice, ids, gqlFields),
});

export const subscribeSDInvoicesByIDs = registerSubscription({
  operationName: 'subscribeSDInvoicesByIDs',
  type: () => [SDInvoice],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<SDInvoice>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(SDInvoice, ids, gqlFields),
});

export const getAllSDInvoices = registerQuery({
  operationName: 'getAllSDInvoices',
  type: () => [SDInvoice],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<SDInvoice>>(gqlFields: SelSet) =>
      readAllEntities(SDInvoice, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllSDInvoices = registerSubscription({
  operationName: 'subscribeAllSDInvoices',
  type: () => [SDInvoice],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<SDInvoice>>(gqlFields: SelSet) =>
      watchAllEntities(SDInvoice, gqlFields, { skip, limit, excludeIDs }),
});

export const getSDInvoiceByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<SDInvoice>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getSDInvoicesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeSDInvoiceByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<SDInvoice>>(gqlFields: SelSet) =>
    id ? subscribeSDInvoicesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
